import React, { useState } from 'react';
import {  View, Text, TextInput, StyleSheet, ActivityIndicator } from 'react-native';
import Modal from 'react-native-modal';   // instead of React-Native’s Modal

import { useAppTheme } from '../../design-system/ThemeProvider';
import { t } from '../../i18n/translations';
import { supabase } from '../../shared/services/supabaseService';
import PrimaryButton from '../../shared/ui/PrimaryButton';
import { useLanguageStore } from '../quiz-core/languageStore';


interface CreateFriendsGroupModalProps {
  visible: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const CreateFriendsGroupModal: React.FC<CreateFriendsGroupModalProps> = ({ visible, onClose, onCreated }) => {
  const { colors } = useAppTheme();
  const { language } = useLanguageStore();
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!groupName.trim()) {
      setError(t('familiesFriends.errorGroupNameRequired', language));
      return;
    }
    setLoading(true);
    setError(null);
    try {
            // Insert directly (RPC unreliable in web dev env)
      const { data: { user } } = await supabase.auth.getUser();
      const uid = user?.id;
      if (!uid) throw new Error('Not authenticated');
      // Check limit (max 3 groups)
      const { count: membershipCount, error: countErr } = await supabase
        .from('friends_members')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', uid);
      if (countErr) throw countErr;
      if ((membershipCount ?? 0) >= 3) {
        throw new Error('You can belong to up to 3 friends groups. Please leave one before creating a new group.');
      }
      // 1. create the friends row
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      const { data: newGroup, error: insertErr } = await supabase
        .from('friends')
        .insert({ name: groupName, description: '', group_code: code, created_by: uid })
        .select('id')
        .single();
      if (insertErr) throw insertErr;
      // 2. add current user as owner
      const { error: memberErr } = await supabase
        .from('friends_members')
        .insert({ group_id: newGroup.id, user_id: uid, role: 'owner' });
      if (memberErr) throw memberErr;

      setGroupName('');
      onCreated();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create friends group.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isVisible={visible} onBackdropPress={onClose}>
      <View style={styles.overlay}>
        <View style={[styles.modal, { backgroundColor: colors.surfaceContainerLow, borderColor: colors.outlineVariant, borderWidth: 1 }]}>
          <Text style={[styles.title, { color: colors.onSurface }]}>{t('familiesFriends.titleCreateFriendsGroup', language)}</Text>
          <TextInput
            placeholder={t('familiesFriends.placeholderGroupName', language)}
            value={groupName}
            onChangeText={setGroupName}
            style={[styles.input, { backgroundColor: colors.surfaceContainerHigh, color: colors.onSurface, borderColor: colors.outlineVariant }]}
            placeholderTextColor={colors.onSurfaceVariant}
            editable={!loading}
          />
          {error && <Text style={styles.error}>{error}</Text>}
          {loading ? (
            <ActivityIndicator color="#3381c6" />
          ) : (
            <PrimaryButton title={t('familiesFriends.buttonCreate', language)} onPress={handleCreate} />
          )}
          <PrimaryButton title={t('familiesFriends.buttonCancel', language)} onPress={onClose} style={[styles.cancelBtn, { backgroundColor: colors.surfaceContainerHigh, borderColor: colors.outlineVariant, borderWidth: 1 }]} />
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    borderRadius: 18,
    padding: 28,
    width: '85%',
    alignItems: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 18,
  },
  input: {
    borderRadius: 10,
    borderWidth: 1,
    padding: 12,
    fontSize: 16,
    width: '100%',
    marginBottom: 12,
  },
  error: {
    color: '#ff6b6b',
    marginBottom: 8,
    textAlign: 'center',
  },
  cancelBtn: {
    marginTop: 10,
  },
  cancelText: {
    color: '#3381c6',
    fontSize: 16,
  },
});

export default CreateFriendsGroupModal;
